import {
  ref,
  computed,
  inject,
  getCurrentInstance,
} from '@vue/composition-api'
import { compact } from 'lodash'
import { useEstimatedSlipPrint } from '@yonarezi/yonarezi3.slip-print'
import { removePortFromIpAddress } from '@/utils/removePortFromIpAddress'
import { wrapPrinterReceive } from '@/utils/sentryReportPrinterError'
import { canExecutePrintOnThisDevice } from '@/views/composable/useReceiptPrinterDeviceSetting'
import useDateFormat from '@/views/composable/useDateFormat'
import useCurrentData from '@/views/composable/useCurrentData'
import useSlipFooterItems from '@/views/composable/useSlipFooterItems'
import useSdpSlipPrint, { buildEstimationTargetInputs } from '@/views/composable/useSdpSlipPrint'

// NOTE: 延長候補の概算伝票印刷（旧ExtensionCandidateCoursesから移設）
export default ({
  props,
  extensionCandidates,
  estimatedTotals,
  getTotalTax,
}) => {
  const vm = getCurrentInstance().proxy

  const courses = inject('courses', [])
  const nominations = inject('nominations', [])
  const items = inject('items', [])
  const users = inject('users', [])
  const printer = inject('printer', null)
  const hallId = inject('hallId', ref(null))

  const { hhmm } = useDateFormat()
  const { currentClub } = useCurrentData()

  const { footerItems, fetchFooterItems } = useSlipFooterItems()

  const estimatedSlipOptions = computed(() => {
    const estimationTargets = [...estimatedTotals.value]
      .sort((a, b) => a.amount - b.amount)
      .map(estimatedTotalObj => {
        const extensionCandidate = extensionCandidates.value[estimatedTotalObj.extensionCandidateIdx]
        const vendingHistoriesTemp = compact([
          extensionCandidate.courseHistoryTemp,
          ...(extensionCandidate.nominationHistoriesTemp || []),
          ...(extensionCandidate.itemHistoriesTemp || []),
          ...(extensionCandidate.newHistoriesTemp || []),
        ])

        return {
          estimatedTotal: estimatedTotalObj.amount,
          estimatedTax: getTotalTax(vendingHistoriesTemp),
          vendingHistoriesTemp,
        }
      })

    return {
      ipAddress: removePortFromIpAddress(currentClub.value.printIpAddress),
      startAt: hhmm(props.startAt),
      expireAt: props.expireAt ? hhmm(props.expireAt) : '',
      tableCode: props.customName || [props.tablePrefix, props.tableNumber, props.tableSuffix].join(''),
      customerCount: props.customerCount.toLocaleString(),
      total: props.total,
      paidInAdvanceAmount: props.paidInAdvanceAmount,
      totalDepositAmount: props.totalDepositAmount,
      vendibles: compact(
        [
          ...(courses.value || []),
          ...(nominations.value || []),
          ...(items.value || []),
        ],
      ),
      recipients: users.value,
      estimationTargets,
      nominationAlias: currentClub.value.nominationAlias,
      courseAlias: currentClub.value.courseAlias,
      printer: printer.value,
      footerItems: footerItems.value,
    }
  })
  const { send: sendEstimatedSlip, onReceive, svg: slipSvg } = useEstimatedSlipPrint(estimatedSlipOptions)

  const buildFooterContext = () => ({
    REAL_TOTAL: Number(props.total) || 0,
    SUBTOTAL: Number(props.total) || 0,
    TOTAL: Number(props.total) || 0,
    SERVICE_FEE: 0,
    PROCESSING_FEE: 0,
    DISCOUNT_AMOUNT: 0,
    CASH_PAYMENT_AMOUNT: 0,
    CARD_PAYMENT_AMOUNT: 0,
    CUSTOMER_COUNT: Number(props.customerCount) || 0,
  })

  // プレビューにもフッターを反映するため、setup時に事前取得
  fetchFooterItems({
    hallId: hallId.value, tableId: props.tableId, slipType: 'estimated', context: buildFooterContext(),
  })

  const { isSdpEnabled, requestPrint: requestSdpPrint } = useSdpSlipPrint({ toast: vm.$toast })

  const printEstimatedSlip = async () => {
    if (!canExecutePrintOnThisDevice()) return

    // SDP有効店舗はサーバー経由で印刷。概算ターゲット（未確定注文）のみフロントで組み立てて渡す
    if (isSdpEnabled()) {
      await requestSdpPrint({
        ticketType: 'estimated',
        tableId: props.tableId,
        inputs: {
          expire_at: props.expireAt ? hhmm(props.expireAt) : '',
          estimation_targets: buildEstimationTargetInputs(estimatedSlipOptions.value.estimationTargets, {
            vendibles: estimatedSlipOptions.value.vendibles,
            recipients: estimatedSlipOptions.value.recipients,
            courseAlias: currentClub.value.courseAlias,
            nominationAlias: currentClub.value.nominationAlias,
          }),
        },
      })

      return
    }

    await fetchFooterItems({
      hallId: hallId.value, tableId: props.tableId, slipType: 'estimated', context: buildFooterContext(),
    })
    await sendEstimatedSlip()
  }

  onReceive.value = wrapPrinterReceive(null, {
    kind: 'receipt',
    slipKind: 'estimated',
    ipAddress: removePortFromIpAddress(currentClub.value?.printIpAddress || ''),
    clubId: currentClub.value?.id,
    printerName: 'レシートプリンタ',
    toast: vm.$toast,
  })

  return {
    slipSvg,
    printEstimatedSlip,
  }
}
